import PropTypes from 'prop-types';
import React from 'react';

import { Badge } from '../../../components/ui/Badge';
import EmptyState from '../../../components/ui/EmptyState';
import Skeleton from '../../../components/ui/Skeleton';

const METHOD_ICONS = {
  print: '🖨️',
  email: '📧',
  mail: '📮',
};

const STATUS_VARIANTS = {
  pending: 'outline',
  sent: 'secondary',
  delivered: 'default',
  failed: 'destructive',
  processing: 'outline',
  cancelled: 'outline',
};

function DeliveryHistoryItem({ delivery }) {
  const icon = METHOD_ICONS[delivery.deliveryMethod] || '📦';
  const variant = STATUS_VARIANTS[delivery.status] || 'outline';
  const electionName = delivery.guide?.election?.name || 'Unknown election';
  const requestedAt = new Date(delivery.requestedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-neutral-200 last:border-0">
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none mt-0.5">{icon}</span>
        <div>
          <p className="font-semibold text-neutral-900">{electionName}</p>
          <p className="text-sm text-neutral-500 capitalize">
            {delivery.deliveryMethod} &middot; {requestedAt}
          </p>
          {delivery.lobTrackingUrl && (
            <a
              href={delivery.lobTrackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline mt-0.5 inline-block"
            >
              Track delivery
            </a>
          )}
        </div>
      </div>
      <Badge variant={variant} className="shrink-0 capitalize">
        {delivery.status}
      </Badge>
    </div>
  );
}

DeliveryHistoryItem.propTypes = {
  delivery: PropTypes.shape({
    id: PropTypes.string.isRequired,
    deliveryMethod: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    requestedAt: PropTypes.string.isRequired,
    lobTrackingUrl: PropTypes.string,
    guide: PropTypes.shape({
      election: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
};

function DeliveryHistoryList({ deliveries, isLoading }) {
  if (isLoading) {
    return (
      <div className="space-y-4 mt-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!deliveries || deliveries.length === 0) {
    return (
      <EmptyState
        icon="inbox"
        title="No deliveries yet"
        description="Your ballot guide delivery history will appear here."
        className="mt-6"
      />
    );
  }

  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold text-neutral-900 mb-2">Delivery History</h2>
      <div className="border border-neutral-200 rounded-lg px-4">
        {deliveries.map((delivery) => (
          <DeliveryHistoryItem key={delivery.id} delivery={delivery} />
        ))}
      </div>
    </div>
  );
}

DeliveryHistoryList.propTypes = {
  deliveries: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
  isLoading: PropTypes.bool,
};

DeliveryHistoryList.defaultProps = {
  deliveries: null,
  isLoading: false,
};

export default DeliveryHistoryList;
